import React, { FC, useCallback, useEffect, useState } from 'react';

import { Alert, Button, Label, Link, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { attachOauth, createOrUpdateOAuthApp, deattachOauth, getOAuthApp } from 'client/api';
import editStyles from 'client/blocks/AliceAgentAppPage/AliceAgentAppEditPage.module.scss';
import { Field } from 'client/blocks/form/Field/Field';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import { UpdateOAuthAppSettings } from 'client/model/oauth';
import { Channel } from 'client/model/skill';
import { getErrorMessage } from 'common/utils/errorsHandler';
import * as accountLinkingKeyset from 'i18n/AccountLinking';
import * as agentKeyset from 'i18n/AliceExternalAppForm';
import * as commonKeyset from 'i18n/Common';
import isEmpty from 'lodash/isEmpty';

import styles from './AliceExternalAppAccountLinking.module.scss';
import { useAppForm } from './formHook';

const t = i18n(accountLinkingKeyset);
const tAgent = i18n(agentKeyset);
const tCommon = i18n(commonKeyset);

/**
 * `AGENT_CHANNEL` is not in the global `Channel` enum (agents are routed through the v3 API
 * surface, not the legacy `aliceAppServicesByChannel` map). The backend's `linkOAuthAppToSkill`
 * resolves channels through a string match, so a literal cast is safe.
 */
const AGENT_CHANNEL = 'aliceAgentApp' as unknown as Channel;

const EMPTY_FIELD_PLACEHOLDER = '*****************';
const DOCS = 'https://tools.ietf.org/html/rfc6749#section-';

interface Props {
    app: AliceAgentAppDTO;
    onSaved: () => Promise<unknown> | void;
    disabled: boolean;
}

type AccountLinkingFormState = UpdateOAuthAppSettings;

const emptyAccountLinking = (): AccountLinkingFormState => ({
    name: '',
    clientId: '',
    clientSecret: '',
    authorizationUrl: '',
    tokenUrl: '',
    refreshTokenUrl: '',
    scope: '',
    yandexClientId: '',
});

interface AccountLinkingFormProps {
    skillId: string;
    skillName: string;
    initial: AccountLinkingFormState;
    /** `null` while no OAuth-app is attached yet; the form treats that as "creating". */
    oauthAppId: string | null;
    disabled: boolean;
    detachDisabled: boolean;
    onSaved: () => Promise<unknown> | void;
    onPersisted: (saved: AccountLinkingFormState) => void;
    onDetached: () => void;
}

/**
 * Inner form. Owns the TanStack-form instance; the outer container is expected to remount it
 * (via `key`) whenever the loaded OAuth-app changes — same trick the legacy form uses with
 * `key={oauthApp.id}`. Without that remount `useAppForm` would freeze on its first
 * `defaultValues` and the user would see empty fields after a page reload.
 */
const AccountLinkingForm: FC<AccountLinkingFormProps> = ({
    skillId,
    skillName,
    initial,
    oauthAppId,
    disabled,
    detachDisabled,
    onSaved,
    onPersisted,
    onDetached,
}) => {
    const { add: toasterAdd } = useToaster();

    const form = useAppForm({
        defaultValues: initial,
        onSubmit: async ({ value, formApi }) => {
            try {
                const isCreating = !value.id;
                const saved = await createOrUpdateOAuthApp({
                    ...value,
                    name: value.name || skillName,
                });
                if (isCreating) {
                    await attachOauth(skillId, saved.id, AGENT_CHANNEL);
                }
                await onSaved();
                toasterAdd({ title: tCommon('save'), theme: 'success', name: 'SAVE_OAUTH_APP' });
                // The next render of the outer container will remount with the new `key`, so
                // `formApi.reset` here is just a safety net for the rare case `onSaved` doesn't
                // bump `oauthAppId` (e.g. the channel-attach call no-ops on retry).
                formApi.reset({ ...saved, clientSecret: '' });
                onPersisted({ ...saved, clientSecret: '' });
            } catch (err) {
                toasterAdd({
                    title: getErrorMessage(err) ?? t('error-occured'),
                    theme: 'danger',
                    name: 'SAVE_OAUTH_APP',
                });
            }
        },
    });

    const { Field: FormField } = form;

    const handleDetach = async () => {
        if (!oauthAppId) return;
        try {
            await deattachOauth(skillId, AGENT_CHANNEL);
            await onSaved();
            onDetached();
            toasterAdd({ title: tCommon('save'), theme: 'success', name: 'DETACH_OAUTH_APP' });
        } catch (err) {
            toasterAdd({
                title: getErrorMessage(err) ?? t('error-occured'),
                theme: 'danger',
                name: 'DETACH_OAUTH_APP',
            });
        }
    };

    const isTouched = form.state.isDirty;
    const isSubmitting = form.state.isSubmitting;

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
            }}
        >
            <section className={styles.aliceAppAccountLinkingForm__content}>
                <h2 className={styles.aliceAppAccountLinkingForm__pageTitle}>{t('account-linking')}</h2>
                {isTouched && oauthAppId && (
                    <Alert
                        className={styles.aliceAppAccountLinkingForm__alert}
                        theme="warning"
                        title={
                            <span className={styles.aliceAppAccountLinkingForm__alertTitle}>
                                {t('warning-settings')}
                            </span>
                        }
                    />
                )}
                {detachDisabled && oauthAppId && (
                    <Alert
                        className={styles.aliceAppAccountLinkingForm__alert}
                        theme="warning"
                        title={
                            <span className={styles.aliceAppAccountLinkingForm__alertTitle}>
                                {tAgent('account-linking-detach-published')}
                            </span>
                        }
                    />
                )}
                <div className={styles.aliceAppAccountLinkingForm__warningsBlock}>
                    {!isEmpty(form.state.errors) && form.state.errors.length > 0 && (
                        <Label theme="danger">{t('error-form')}</Label>
                    )}
                </div>

                <FormField name="clientId">
                    {(field) => (
                        <Field
                            required
                            fieldTitle={t('label-client_id')}
                            error={field.state.meta.errors[0] as string | undefined}
                            fieldDescription={
                                <Link href={`${DOCS}2.2`} target="_blank">
                                    Client Identifier
                                </Link>
                            }
                        >
                            <TextInput
                                className={styles.aliceAppAccountLinkingForm__formElement}
                                value={field.state.value ?? ''}
                                onUpdate={field.handleChange}
                                size="l"
                                disabled={disabled}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="clientSecret">
                    {(field) => (
                        <Field
                            required={!oauthAppId}
                            fieldTitle={t('label-client_secret')}
                            error={field.state.meta.errors[0] as string | undefined}
                            fieldDescription={
                                <Link href={`${DOCS}2.3.1`} target="_blank">
                                    Client Password
                                </Link>
                            }
                        >
                            <TextInput
                                className={styles.aliceAppAccountLinkingForm__formElement}
                                value={field.state.value ?? ''}
                                onUpdate={field.handleChange}
                                placeholder={oauthAppId && !field.state.value ? EMPTY_FIELD_PLACEHOLDER : undefined}
                                size="l"
                                disabled={disabled}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="authorizationUrl">
                    {(field) => (
                        <Field
                            required
                            fieldTitle={t('label-auth_url')}
                            error={field.state.meta.errors[0] as string | undefined}
                            fieldDescription={
                                <Link href={`${DOCS}3.1`} target="_blank">
                                    API authorization endpoint
                                </Link>
                            }
                        >
                            <TextInput
                                className={styles.aliceAppAccountLinkingForm__formElement}
                                value={field.state.value ?? ''}
                                onUpdate={field.handleChange}
                                type="url"
                                size="l"
                                disabled={disabled}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="tokenUrl">
                    {(field) => (
                        <Field
                            required
                            fieldTitle={t('label-token_url')}
                            error={field.state.meta.errors[0] as string | undefined}
                            fieldDescription={
                                <Link href={`${DOCS}3.2`} target="_blank">
                                    Token Endpoint
                                </Link>
                            }
                        >
                            <TextInput
                                className={styles.aliceAppAccountLinkingForm__formElement}
                                value={field.state.value ?? ''}
                                onUpdate={field.handleChange}
                                type="url"
                                size="l"
                                disabled={disabled}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="refreshTokenUrl">
                    {(field) => (
                        <Field
                            fieldTitle={t('label-refresh_token_url')}
                            error={field.state.meta.errors[0] as string | undefined}
                            fieldDescription={
                                <Link href={`${DOCS}6`} target="_blank">
                                    Refreshing an Access Token
                                </Link>
                            }
                        >
                            <TextInput
                                className={styles.aliceAppAccountLinkingForm__formElement}
                                value={field.state.value ?? ''}
                                onUpdate={field.handleChange}
                                type="url"
                                size="l"
                                disabled={disabled}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="scope">
                    {(field) => (
                        <Field
                            fieldTitle={t('label-scope')}
                            error={field.state.meta.errors[0] as string | undefined}
                            fieldDescription={
                                <Link href={`${DOCS}3.3`} target="_blank">
                                    Access Token Scope
                                </Link>
                            }
                        >
                            <TextInput
                                className={styles.aliceAppAccountLinkingForm__formElement}
                                value={field.state.value ?? ''}
                                onUpdate={field.handleChange}
                                size="l"
                                disabled={disabled}
                            />
                        </Field>
                    )}
                </FormField>
            </section>

            {/* Reuse the EditPage footer styles (`position: sticky; bottom: 0`) so the Save
                button matches the main tab — pinned to the bottom, left-aligned, follows scroll. */}
            <div className={editStyles.footer}>
                <Button type="submit" view="action" size="l" loading={isSubmitting} disabled={disabled}>
                    {tCommon('save')}
                </Button>
                {oauthAppId && (
                    <Button
                        view="outlined-danger"
                        size="l"
                        onClick={handleDetach}
                        disabled={disabled || detachDisabled}
                    >
                        {tAgent('account-linking-detach')}
                    </Button>
                )}
            </div>
        </form>
    );
};

/**
 * "Связка аккаунтов" tab for external Alice agents. The visual layout is a 1:1 copy of the
 * legacy `AliceAppAccountLinkingForm` so the two tabs feel identical to a developer who's
 * already used the AliceSkill version. Wiring differs: state is owned by TanStack-form and the
 * channel sent on `attachOauth`/`deattachOauth` is `aliceAgentApp`.
 *
 * Lifecycle:
 *   - On mount, if the skill already has `draft.oauthAppId`, fetch the OAuth-app and pre-fill
 *     the form. `clientSecret` is intentionally never returned by the server — it's left empty
 *     locally and a placeholder hints that the previous value is preserved on save.
 *   - The inner `AccountLinkingForm` is remounted via `key={oauthAppId ?? 'new'}` whenever the
 *     attached OAuth-app changes (page reload, save, detach). This mirrors the legacy form's
 *     `key={oauthApp.id}` trick and is the only way to push fresh `defaultValues` into
 *     TanStack-form — `useAppForm` freezes on its first `defaultValues` after mount.
 *   - Save → `createOrUpdateOAuthApp`. On create, the new id is also linked via `attachOauth`.
 *   - Detach is available only while the agent is off-air. It calls `deattachOauth`, then the
 *     container's `initial` and `oauthAppId` reset to empty/null and the remount clears the form.
 */
export const AliceExternalAppAccountLinking: FC<Props> = ({ app, onSaved, disabled }) => {
    const { add: toasterAdd } = useToaster();
    const oauthAppId = app.draft.oauthAppId ?? null;

    const [isLoading, setIsLoading] = useState<boolean>(Boolean(oauthAppId));
    const [initial, setInitial] = useState<AccountLinkingFormState>(emptyAccountLinking);

    const loadOAuthApp = useCallback(
        async (id: string) => {
            try {
                setIsLoading(true);
                const remote = await getOAuthApp(id);
                setInitial({
                    ...remote,
                    // clientSecret is never returned by the server; keep empty and surface a
                    // placeholder so the developer knows their previous value will be retained.
                    clientSecret: '',
                });
            } catch (err) {
                toasterAdd({
                    title: getErrorMessage(err) ?? t('error-occured'),
                    theme: 'danger',
                    name: 'LOAD_OAUTH_APP',
                });
            } finally {
                setIsLoading(false);
            }
        },
        [toasterAdd],
    );

    useEffect(() => {
        if (oauthAppId) {
            void loadOAuthApp(oauthAppId);
        } else {
            setIsLoading(false);
            setInitial(emptyAccountLinking());
        }
    }, [oauthAppId, loadOAuthApp]);

    if (isLoading) return <PageLoader />;

    return (
        <AccountLinkingForm
            key={oauthAppId ?? 'new'}
            skillId={app.skillId}
            skillName={app.draft.name}
            initial={initial}
            oauthAppId={oauthAppId}
            disabled={disabled}
            detachDisabled={app.onAir}
            onSaved={onSaved}
            onPersisted={setInitial}
            onDetached={() => setInitial(emptyAccountLinking())}
        />
    );
};
